import { useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send, CornerDownRight } from "lucide-react";
import { PHASES } from "../../lib/phases";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

const TASK_LABEL_LOOKUP = Object.fromEntries(
  PHASES.flatMap((p) => p.tasks.map((t) => [t.id, t.label]))
);

function ReplyForm({ listingId, commentId, onReplied }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listingId}/collaborator-comments/${commentId}/replies`, { text: trimmed });
      onReplied(data);
      toast.success("Reply posted — collaborator will see it on their next hub visit");
      setText(""); setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reply");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`reply-toggle-${commentId}`}
        className="text-[11px] font-semibold uppercase tracking-widest text-[#010082] hover:underline"
      >
        Reply inline
      </button>
    );
  }
  return (
    <div className="mt-2" data-testid={`reply-form-${commentId}`}>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Reply visible to this collaborator only. Stays off email."
        rows={2}
        className="text-xs bg-white border-stone-300 leading-relaxed"
        data-testid={`reply-input-${commentId}`}
      />
      <div className="mt-2 flex items-center justify-end gap-2">
        <Button
          variant="outline" size="sm"
          onClick={() => { setOpen(false); setText(""); }}
          className="h-8 rounded-full border-stone-300 text-xs"
        >Cancel</Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={busy || !text.trim()}
          className="h-8 rounded-full text-white text-xs px-4"
          style={{ backgroundColor: "#010082" }}
          data-testid={`reply-submit-${commentId}`}
        >
          <Send className="h-3 w-3 mr-1" />
          {busy ? "Posting…" : "Post reply"}
        </Button>
      </div>
    </div>
  );
}

export default function CollaboratorCommentsFeed({ listing, onListingUpdate }) {
  const comments = useMemo(() => {
    const list = listing?.collaborator_comments || [];
    return [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [listing?.collaborator_comments]);

  if (!comments.length) {
    return (
      <div className="rounded-md border border-dashed border-stone-300 bg-white p-6 mb-6 text-center" data-testid="collaborator-comments-empty">
        <MessageSquare className="h-5 w-5 text-stone-400 mx-auto" />
        <p className="text-sm text-stone-500 mt-2 leading-relaxed max-w-md mx-auto">
          No notes from the buyer side yet. Invited collaborators can drop a question on any milestone — it'll land here, not in your inbox.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-stone-200 bg-white p-6 mb-6" data-testid="collaborator-comments-feed">
      <p className="overline text-stone-500 mb-2">Buyer-side notes</p>
      <h3 className="font-heading text-xl font-semibold text-stone-900 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-stone-400" />
        Milestone comments
      </h3>
      <ul className="mt-4 divide-y divide-stone-100">
        {comments.map((c) => {
          const replies = c.replies || [];
          return (
            <li key={c.id} className="py-4" data-testid={`collaborator-comment-${c.id}`}>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-sm font-semibold text-stone-900">{c.author_name}</span>
                <span className="inline-flex items-center rounded-full border border-stone-300 bg-stone-50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-stone-600 font-semibold">
                  {c.author_role_label || c.author_role}
                </span>
                <span className="text-[11px] text-stone-400 ml-auto">
                  {new Date(c.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              </div>
              <div className="text-[11px] text-stone-500 mt-0.5">
                On: <span className="font-medium text-stone-700">{TASK_LABEL_LOOKUP[c.task_id] || c.task_id}</span>
              </div>
              <p className="text-sm text-stone-800 mt-1.5 whitespace-pre-wrap leading-relaxed">{c.text}</p>

              {replies.length > 0 && (
                <ul className="mt-3 ml-4 border-l-2 border-[#010082]/20 pl-4 space-y-3" data-testid={`replies-${c.id}`}>
                  {replies.map((r) => (
                    <li key={r.id} data-testid={`reply-${r.id}`}>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <CornerDownRight className="h-3 w-3 text-[#010082] shrink-0" />
                        <span className="text-xs font-semibold text-stone-900">{r.author_name}</span>
                        <span className="inline-flex items-center rounded-full bg-[#010082]/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-[#010082] font-semibold">
                          {r.author_role_label || "Listing Agent"}
                        </span>
                        <span className="text-[10px] text-stone-400 ml-auto">
                          {new Date(r.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="text-sm text-stone-800 mt-1 whitespace-pre-wrap leading-relaxed">{r.text}</p>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-3">
                <ReplyForm
                  listingId={listing.id}
                  commentId={c.id}
                  onReplied={(updated) => onListingUpdate && onListingUpdate(updated)}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
